/**
 * @fileoverview Own workspace selection semantics for visible rows and all matching documents. Query-wide selection uses exclusions rather than assuming loaded rows are the complete result set. The parent owns query identity and bulk execution; this layer supplies selection state and explicit controls. Backend operations must still resolve and authorize actual targets.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppIcon from '../shared/AppIcon';



function itemId(item) {
  return String(item?.document_id || item?.folder_id || item?.item_id || '');
}

/**
 * All-matching selection represents a query scope plus exclusions, not a captured list of every document. Callers must preserve query identity and resolve permitted targets when executing an action.
 */
export function useDocumentSelection({ items, total, queryKey, allowAllMatching = true }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const anchorRef = useRef('');
  const queryKeyRef = useRef(queryKey);

  useEffect(

  () => {
    if (queryKeyRef.current === queryKey) return;
    queryKeyRef.current = queryKey;
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setAllMatching(false);
    anchorRef.current = '';
  }, [queryKey]);

  const visibleIds = useMemo(() => items.map(itemId).filter(Boolean), [items]);
  const isSelected = useCallback(

  (id) => (
    allMatching ? !excludedIds.has(String(id)) : selectedIds.has(String(id))
  ), [allMatching, excludedIds, selectedIds]);

  const toggle = useCallback(

  (id, options = {}) => {
    const normalizedId = String(id || '');
    if (!normalizedId) return;
    const rangeRequested = Boolean(options.range && anchorRef.current);
    const rangeStart = visibleIds.indexOf(anchorRef.current);
    const rangeEnd = visibleIds.indexOf(normalizedId);
    const targetIds = rangeRequested && rangeStart >= 0 && rangeEnd >= 0
      ? visibleIds.slice(Math.min(rangeStart, rangeEnd), Math.max(rangeStart, rangeEnd) + 1)
      : [normalizedId];
    if (allMatching) {
      setExcludedIds(

      (current) => {
        const next = new Set(current);
        const shouldSelect = next.has(normalizedId);
        targetIds.forEach((targetId) => shouldSelect ? next.delete(targetId) : next.add(targetId));
        return next;
      });
    } else {
      setSelectedIds(

      (current) => {
        const next = new Set(current);
        const shouldSelect = !next.has(normalizedId);
        targetIds.forEach((targetId) => shouldSelect ? next.add(targetId) : next.delete(targetId));
        return next;
      });
    }
    anchorRef.current = normalizedId;
  }, [allMatching, visibleIds]);

  const selectPage = useCallback(

  () => {
    if (allMatching) {
      setExcludedIds((current) => {
        const next = new Set(current);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((current) => new Set([...current, ...visibleIds]));
    }
  }, [allMatching, visibleIds]);

  const deselectPage = useCallback(

  () => {
    if (allMatching) {
      setExcludedIds((current) => new Set([...current, ...visibleIds]));
    } else {
      setSelectedIds((current) => {
        const next = new Set(current);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [allMatching, visibleIds]);

  const clear = useCallback(

  () => {
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setAllMatching(false);
    anchorRef.current = '';
  }, []);

  const selectAllMatching = useCallback(

  () => {
    if (!allowAllMatching) return;
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setAllMatching(true);
  }, [allowAllMatching]);

  const count = allMatching
    ? Math.max(0, Number(total || 0) - excludedIds.size)
    : selectedIds.size;
  const pageSelectedCount = visibleIds.filter(isSelected).length;
  const pageAllSelected = visibleIds.length > 0 && pageSelectedCount === visibleIds.length;
  const selectedVisibleItems = items.filter((item) => isSelected(itemId(item)));
  const payload = useMemo(() => allMatching ? {
    mode: 'query',
    excluded_ids: [...excludedIds],
  } : {
    mode: 'items',
    items: [...selectedIds].map((id) => ({ item_type: 'document', item_id: id })),
  }, [allMatching, excludedIds, selectedIds]);

  return {
    allMatching,
    count,
    pageAllSelected,
    pageSelectedCount,
    payload,
    selectedVisibleItems,
    isSelected,
    toggle,
    selectPage,
    deselectPage,
    selectAllMatching,
    clear,
  };
}



function commonCapability(items, capability) {
  return items.length > 0 && items.every((item) => item?.capabilities?.[capability] === true);
}



function selectionCapability(selection, capabilitySummary, capability) {
  const summaryValue = capabilitySummary?.[capability];
  if (typeof summaryValue === 'boolean') return summaryValue;
  const inspectedItems = selection.selectedVisibleItems;
  // Query selections are validated item-by-item by the operation endpoint. A
  // server summary can make the toolbar exact; until then the visible page is
  // used for affordances and partial failures remain explicit to the user.
  return commonCapability(inspectedItems, capability);
}



export default function BulkSelectionBar({
  selection,
  total,
  allowAllMatching,
  busy,
  onMove,
  onCopy,
  onDownload,
  onTrash,
  capabilitySummary,
}) {
  if (!selection.count) return null;
  const capabilities = {
    move: selectionCapability(selection, capabilitySummary, 'can_move'),
    copy: selectionCapability(selection, capabilitySummary, 'can_copy'),
    download: selectionCapability(selection, capabilitySummary, 'can_download'),
    trash: selectionCapability(selection, capabilitySummary, 'can_delete'),
  };
  const suffix = selection.allMatching ? ' matching this view' : '';
  const unavailableLabel =

  (action, available) => {
    if (available) return action;
    const reason = `${action} is not available for every selected document`;
    return `${action} unavailable: ${reason}`;
  };

  return (
    <div className="documents-bulk-selection" role="region" aria-label="Bulk document actions" aria-busy={busy || undefined}>
      <div className="documents-bulk-selection-summary" role="status" aria-live="polite">
        <strong>{selection.count} selected{suffix}</strong>
        {!selection.allMatching && selection.count < total && allowAllMatching && (
          <button type="button" onClick={selection.selectAllMatching} disabled={busy}>
            Select all {total} matches
          </button>
        )}
      </div>
      <div className="documents-bulk-selection-actions">
        <button type="button" onClick={onMove} disabled={busy || !capabilities.move} aria-label={unavailableLabel('Move', capabilities.move)}>
          <AppIcon icon="pi pi-folder" aria-hidden="true" /> Move
        </button>
        <button type="button" onClick={onCopy} disabled={busy || !capabilities.copy} aria-label={unavailableLabel('Copy', capabilities.copy)}>
          <AppIcon icon="pi pi-copy" aria-hidden="true" /> Copy
        </button>
        <button type="button" onClick={onDownload} disabled={busy || !capabilities.download} aria-label={unavailableLabel('Download', capabilities.download)}>
          <AppIcon icon="pi pi-download" aria-hidden="true" /> Download
        </button>
        <button type="button" className="is-danger" onClick={onTrash} disabled={busy || !capabilities.trash} aria-label={unavailableLabel('Trash', capabilities.trash)}>
          <AppIcon icon="pi pi-trash" aria-hidden="true" /> Trash
        </button>
        <button type="button" className="is-clear" onClick={selection.clear} disabled={busy}>
          Clear
        </button>
      </div>
    </div>
  );
}
